import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/endpoints/auth'
import type { User, UserRole } from '@/types'
import { resetAllStores } from '../utils/storeReset'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)

  const isInitialized = ref(false)

  let _initAuthPromise: Promise<void> | null = null

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const role = computed<UserRole | null>(() => user.value?.role ?? null)
  const isEmailVerified = computed(() => !!user.value?.email_verified_at)

  const homeRoute = computed<string>(() => {
    switch (user.value?.role) {
      case 'candidate':    return '/candidate/dashboard'
      case 'hrd_admin':   return '/hrd/dashboard'
      case 'interviewer': return '/interviewer/dashboard'
      default:             return '/login'
    }
  })

  async function initAuth(): Promise<void> {
    if (isInitialized.value) return
    if (_initAuthPromise) return _initAuthPromise
    _initAuthPromise = _doInitAuth().finally(() => { _initAuthPromise = null })
    return _initAuthPromise
  }

  async function _doInitAuth(): Promise<void> {
    if (localStorage.getItem('isAuthenticated') !== 'true') {
      try {
        await authApi.initializeCSRF()
      } catch (err) {
        console.warn('[Auth] CSRF initialization failed:', err)
      }
      isInitialized.value = true
      return
    }

    try {
      const refreshPromise = authApi.refresh()
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Auth refresh timeout')), 5000)
      )
      const { data: refreshRes } = await Promise.race([refreshPromise, timeoutPromise])
      accessToken.value = refreshRes.data.access_token

      const { data: meRes } = await authApi.getMe()
      user.value = meRes.data.user
    } catch {
      accessToken.value = null
      user.value = null
      localStorage.removeItem('isAuthenticated')
    } finally {
      isInitialized.value = true
    }
  }

  async function refreshToken(): Promise<void> {
    const { data: res } = await authApi.refresh()
    accessToken.value = res.data.access_token
    localStorage.setItem('isAuthenticated', 'true')
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
      // Ignore API errors during logout
    } finally {
      accessToken.value = null
      user.value = null
      localStorage.removeItem('isAuthenticated')
      resetAllStores()
    }
  }

  function reset(): void {
    user.value = null
    accessToken.value = null
  }

  return {
    user,
    accessToken,
    isInitialized,
    isAuthenticated,
    role,
    isEmailVerified,
    homeRoute,
    initAuth,
    refreshToken,
    logout,
    reset,
  }
})

